/**
 * overlayController — Block 2 control-overlay actions (write side).
 *
 * INIT-2026-Q3-SCENE-HIERARCHY Block 2 (Task A.3 + Task C).
 *
 * Thin imperative wrappers that read the current manifest from
 * `useSceneHierarchyStore`, compute an overlay delta with the PURE helpers in
 * `overlayGraph.ts`, and write it back via the store's `setControlOverlayEntry`
 * / `mergeControlOverlay` actions.
 *
 * Side-effects live ONLY here; all graph math is pure and unit-tested in
 * `overlayGraph.ts`. This keeps the controller trivial and the logic testable.
 *
 * Aspect semantics:
 *   - `isolate(objectId)`        — keep the target + its ANCESTOR path visible;
 *                                  hide everything else (Task A.3).
 *   - `isolateSystem(objectId)`  — keep the target + its SUBTREE visible (Task C);
 *                                  defaults to the functional aspect.
 *   - `colorByAspect(aspect)`    — deterministic colour per top-level group.
 */

import type { UniversalSceneNode } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import { useSceneHierarchyStore } from "@/state/sceneHierarchyStore";
import type { TreeAspect } from "./treeProjection";
import {
  ancestorPathKeepSet,
  computeClearIsolation,
  computeColorByAspect,
  computeIsolation,
  indexNodesById,
  subtreeKeepSet,
} from "./overlayGraph";

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Current manifest nodes from the store, or an empty array when none loaded. */
function currentNodes(): UniversalSceneNode[] {
  return useSceneHierarchyStore.getState().manifest?.nodes ?? [];
}

function allObjectIds(nodes: readonly UniversalSceneNode[]): string[] {
  return nodes.map((n) => n.objectId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Visibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Toggles the `visible` flag for a single object. A missing entry (or one with
 * `visible !== false`) is treated as visible, so the first toggle hides it.
 */
export function toggleVisibility(objectId: string): void {
  const store = useSceneHierarchyStore.getState();
  const current = store.controlOverlay.byObject[objectId];
  const isVisible = current?.visible !== false;
  store.setControlOverlayEntry(objectId, { visible: !isVisible });
}

// ─────────────────────────────────────────────────────────────────────────────
// Isolation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Isolates `objectId`: keeps the target and its ancestor path visible (and
 * marks them `isolated`), hides every other object. (Task A.3)
 *
 * @param objectId target object to isolate.
 * @param aspect   parent graph to walk for the ancestor path. Default "spatial".
 */
export function isolate(
  objectId: string,
  aspect: TreeAspect = "spatial",
): void {
  const nodes = currentNodes();
  const keep = ancestorPathKeepSet(objectId, indexNodesById(nodes), aspect);
  const delta = computeIsolation(keep, allObjectIds(nodes));
  useSceneHierarchyStore.getState().mergeControlOverlay(delta);
}

/**
 * Isolates the SYSTEM rooted at `objectId`: keeps the target and its entire
 * subtree visible, hides everything else. (Task C — "Isolate system")
 *
 * Defaults to the functional aspect, matching the "Isolate system" affordance
 * which operates on the functional-aspect subtree.
 *
 * @param objectId root of the system to isolate.
 * @param aspect   parent graph defining the subtree. Default "functional".
 */
export function isolateSystem(
  objectId: string,
  aspect: TreeAspect = "functional",
): void {
  const nodes = currentNodes();
  const keep = subtreeKeepSet(objectId, nodes, aspect);
  const delta = computeIsolation(keep, allObjectIds(nodes));
  useSceneHierarchyStore.getState().mergeControlOverlay(delta);
}

/**
 * Isolates a block: keeps all objects whose blockCode matches the given code
 * (and their block-group + layer-group ancestors) visible; hides everything else.
 */
export function isolateBlock(blockGroupNodeId: string): void {
  const nodes = currentNodes();
  const blockNode = nodes.find((n) => n.objectId === blockGroupNodeId);
  if (!blockNode) return;
  const blockCode = (blockNode.metadata as Record<string, unknown> | undefined)?.blockCode;
  if (!blockCode) {
    isolateSystem(blockGroupNodeId, "spatial");
    return;
  }

  const keep = new Set<string>();
  keep.add(blockGroupNodeId);
  // Keep layer-group parent
  if (blockNode.parentId) keep.add(blockNode.parentId);
  // Keep all children of this block group
  for (const n of nodes) {
    if (n.parentId === blockGroupNodeId) keep.add(n.objectId);
  }
  const delta = computeIsolation(keep, allObjectIds(nodes));
  useSceneHierarchyStore.getState().mergeControlOverlay(delta);
}

/**
 * Clears any active isolation: restores every object to visible and not
 * isolated. `colorOverride` / `opacity` are left untouched.
 */
export function clearIsolation(): void {
  const nodes = currentNodes();
  const delta = computeClearIsolation(allObjectIds(nodes));
  useSceneHierarchyStore.getState().mergeControlOverlay(delta);
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-object styling
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sets a per-object colour override. `color` must be a design-token ALIAS
 * (e.g. `"color.accent.warning"`), never a raw hex value
 * (scene-hierarchy types §6 — token-only styling rule).
 */
export function setColorOverride(objectId: string, color: string): void {
  useSceneHierarchyStore
    .getState()
    .setControlOverlayEntry(objectId, { colorOverride: color });
}

/**
 * Sets a per-object opacity in the inclusive range [0, 1]. Values outside the
 * range are clamped so callers can pass percentages-as-fractions safely.
 */
export function setOpacity(objectId: string, n: number): void {
  const clamped = Math.max(0, Math.min(1, n));
  useSceneHierarchyStore
    .getState()
    .setControlOverlayEntry(objectId, { opacity: clamped });
}

// ─────────────────────────────────────────────────────────────────────────────
// Color by aspect (Task C)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Assigns deterministic `colorOverride` token aliases to every member of
 * `aspect`, grouped by the member's top-level aspect group. (Task C)
 *
 * @param aspect active aspect to group by. Default "spatial".
 */
export function colorByAspect(aspect: TreeAspect = "spatial"): void {
  const nodes = currentNodes();
  const delta = computeColorByAspect(nodes, aspect);
  useSceneHierarchyStore.getState().mergeControlOverlay(delta);
}
